import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "page-container" }
const _hoisted_2 = { class: "page-header page-header-with-tabs" }
const _hoisted_3 = { class: "page-header-tab-bar flex-wrap gap-2 justify-content-between" }
const _hoisted_4 = { class: "flex flex-wrap gap-2" }
const _hoisted_5 = { class: "page-container" }
const _hoisted_6 = { class: "w-full" }

import { computed, onBeforeMount, ref, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import MultiSelect, { MultiSelectChangeEvent } from 'primevue/multiselect';
import TreeSelect from 'primevue/treeselect';
import { TreeNode } from 'primevue/tree';
import EventsReport from './EventsReport';
import ProgressReport from './ProgressReport';
import PayrollReport from './PayrollReport';
import ReviewReport from './ReviewReport';
import StudentTotalReport from './StudentTotalReport';
import AppTabMenu from '@/components/AppTabMenu';
import { useDateTime, usePersonTypes, useRouting, can, useRequestCancel, useArrayUtilities } from '@/composables';
import { useReportStore, useEventReportStore } from '@/store';
import { PERMISSIONS } from '@/enum';


export default /*@__PURE__*/_defineComponent({
  __name: 'Reports',
  setup(__props) {

const arrayUtilities = useArrayUtilities();

const $routing = useRouting();

const subOrganizationList = ref([] as TreeNode[]);
const eventReportStore = useEventReportStore();

const datesQuery = ref([new Date(), new Date()]);

const reportStore = useReportStore();
const {
  filters: {
    value: { clinicians, organizations },
  },
} = storeToRefs(reportStore);

const items = [
  {
    route: '/reports/',
    name: 'reports.events',
    label: 'Events',
    permission: [PERMISSIONS.REPORTS_VIEW_INDEX],
    component: EventsReport,
    disabled: () => $routing.route.name === 'reports.events',
  },
  {
    route: '/reports/review',
    label: 'Review',
    component: ReviewReport,
    permission: [PERMISSIONS.REPORTS_REVIEW_INDEX],
    disabled: () => $routing.route.name === 'reports.review',
  },
  {
    route: '/reports/progress',
    label: 'Progress',
    component: ProgressReport,
    permission: [PERMISSIONS.REPORTS_PROGRESS_INDEX],
    disabled: () => $routing.route.name === 'reports.progress',
  },
  {
    route: '/reports/payroll',
    label: 'Payment',
    component: PayrollReport,
    permission: [PERMISSIONS.REPORTS_PAYMENT_INDEX],
    disabled: () => $routing.route.name === 'reports.payroll',
  },
  {
    route: '/reports/student-total',
    label: 'Student Total',
    component: StudentTotalReport,
    permission: [PERMISSIONS.REPORTS_STUDENT_TOTAL_INDEX],
    disabled: () => $routing.route.name === 'reports.student-total',
  },
];

onBeforeMount(() => init());

const reportIsPayroll = computed<boolean>(() => $routing.route.name === 'reports.payroll');

const reportIsStudentTotal = computed<boolean>(() => $routing.route.name === 'reports.student-total');

onBeforeUnmount(() => {
  useRequestCancel().requestSaverAbortAll();
  $routing.router.push({ query: {} });
});

const reportsIsEvents = computed<boolean>(() => {
  return $routing.route.name === 'reports.events';
});

const itemsEnabled = computed(() => {
  return items.filter((item) => {
    if (item.permission) {
      return can(item.permission);
    }

    return false;
  });
});

const filteredClinician = ref(false);

const showClearFilters = computed(() => {
  if (reportIsPayroll.value) {
    return Object.keys($routing.route.query).length > 2;
  }

  if (can([PERMISSIONS.REPORTS_SHOW_CLEAR_FILTERS_CLINICIAN])) {
    return Object.keys($routing.route.query).length > 5;
  }
  return Object.keys($routing.route.query).length > 3;
});

const disabledClinicianOptions = computed<string>(() => (usePersonTypes.isClinician() ? 'name' : ''));

const displaySelectedClinicianType = computed<string>(() =>
  can([PERMISSIONS.REPORTS_DISPLAY_SELECTED_CLINICIAN_TYPE]) ? 'comma' : 'chip',
);

const init = async () => {
  await reportStore.defineDefaults();
  const dates = reportStore.baseDates();

  datesQuery.value[0] = dates.start;
  datesQuery.value[1] = dates.end;

  if (can([PERMISSIONS.REPORTS_FILTER_ORGANIZATIONS]))
    await reportStore.getReportOrganizationFilter(dates.start, dates.end);
  if (can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS]))
    await reportStore.getReportServiceProviderFilter(dates.start, dates.end);
};

const onClinicianChange = (event: MultiSelectChangeEvent) => {
  const selectedClinician = event.value;
  filteredClinician.value = selectedClinician.length;

  if (selectedClinician.length) {
    $routing.addQueryParam('clinicians', selectedClinician.join());
  } else {
    reportStore.filters.clinicians.selected = [];
    $routing.removeQueryParam('clinicians');
  }
};

const onClearFilters = async () => {
  const dates = reportStore.baseDates(reportsIsEvents.value);
  const start = dates.start;
  const end = dates.end;
  const startAt = useDateTime().formatDateForApi(start);
  const endAt = useDateTime().formatDateForApi(end);
  const defaultFilters = {
    start_at: startAt,
    end_at: endAt,
  };
  let resetQueryParams =
    can([PERMISSIONS.REPORTS_RSM_INDEX]) && !reportIsPayroll.value
      ? {
          ...defaultFilters,
          is_rsm: 0,
        }
      : defaultFilters;
  const clinicianDefault = can([PERMISSIONS.REPORTS_SHOW_CLEAR_FILTERS_CLINICIAN])
    ? reportStore.filters.clinicians.selected
    : [];
  reportStore.filters.dates = [start, end];
  reportStore.filters.organizations.selected = {};
  reportStore.filters.organizations.isRsm = false;

  if (can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS])) {
    await reportStore.getReportServiceProviderFilter(start, end);
    reportStore.filters.clinicians.selected = [];
  }

  if (clinicianDefault && clinicianDefault.length > 0) {
    resetQueryParams = { ...resetQueryParams, clinicians: clinicianDefault };
  }
  $routing.resetQueryParams(resetQueryParams);
};

const onOrganizationChange = async () => {
  subOrganizationList.value.forEach((org) => {
    if (organizations.selected !== null) {
      organizations.selected[org.key] = true;
    }
  });

  subOrganizationList.value = [];

  reportStore.filters.organizations.isRsm = isThereRsmSelected();

  if (can([PERMISSIONS.REPORTS_CHANGE_ORGANIZATIONS_CLINICIAN])) {
    reportStore.filters.clinicians.selected = [];
    $routing.removeQueryParam('clinicians');
  }

  if (can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS])) {
    const dates = datesQuery.value;
    await reportStore.getReportServiceProviderFilter(dates[0], dates[1]);
  }

  const params = Object.keys(organizations.selected).join();

  if (params.length > 0) {
    $routing.addQueryParam('organizations', params);
  } else {
    $routing.removeQueryParam('organizations');
  }
};

const onOrganizationSelected = async (event: TreeNode) => {
  const subOrganizations = arrayUtilities.useFlatten(event.children);
  subOrganizationList.value = subOrganizations;
};

const dateRangeChange = async (dates: Date[]) => {
  reportStore.onDateRangeChange(dates);
  eventReportStore.filters.searchingByDates = !eventReportStore.filters.searchingByDates;

  datesQuery.value = dates;

  if (can([PERMISSIONS.REPORTS_FILTER_ORGANIZATIONS]))
    await reportStore.getReportOrganizationFilter(dates[0], dates[1]);

  const canChangeClinicians =
    can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS]) &&
    (usePersonTypes.isElumaAdmin() || usePersonTypes.isSchoolAdmin());

  if (canChangeClinicians) {
    await reportStore.getReportServiceProviderFilter(dates[0], dates[1]);
  }
};

const isThereRsmSelected = () => {
  const flatOrganizationList = arrayUtilities.useFlatten(organizations.options);
  const selectedOrganizations = Object.keys(organizations.selected).map((organization) => Number(organization));
  const filter = flatOrganizationList.filter((organization) => selectedOrganizations.includes(organization.key));
  return filter.every((option) => option.isRsm === true);
};

const activeIndex = computed(() => items.findIndex((item) => item.disabled()));

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_AppDateRangePicker = _resolveComponent("AppDateRangePicker")!
  const _component_RouterView = _resolveComponent("RouterView")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[3] || (_cache[3] = _createElementVNode("h1", null, "Reports", -1)),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(AppTabMenu), {
          items: itemsEnabled.value,
          "active-index": activeIndex.value
        }, null, 8, ["items", "active-index"]),
        _withDirectives(_createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_4, [
            (showClearFilters.value)
              ? (_openBlock(), _createBlock(_component_AppButton, {
                  key: 0,
                  type: "secondary",
                  text: "Clear",
                  onClick: onClearFilters
                }))
              : _createCommentVNode("", true),
            (_unref(can)([_unref(PERMISSIONS).REPORTS_FILTER_SERVICE_PROVIDERS]))
              ? (_openBlock(), _createBlock(_unref(TreeSelect), {
                  key: 1,
                  modelValue: _unref(organizations).selected,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(organizations).selected) = $event)),
                  options: _unref(organizations).options,
                  "selection-mode": "multiple",
                  "meta-key-selection": false,
                  display: "chip",
                  placeholder: "All Organizations",
                  disabled: reportIsStudentTotal.value,
                  onChange: onOrganizationChange,
                  onNodeSelect: onOrganizationSelected
                }, null, 8, ["modelValue", "options", "disabled"]))
              : _createCommentVNode("", true),
            _createVNode(_unref(MultiSelect), {
              modelValue: _unref(clinicians).selected,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(clinicians).selected) = $event)),
              options: _unref(clinicians).options,
              loading: _unref(clinicians).loading,
              filter: true,
              "reset-filter-on-hide": true,
              "option-disabled": disabledClinicianOptions.value,
              "remove-token-icon": "m-icon close text-sm",
              "option-label": "name",
              "option-value": "value",
              "show-toggle-all": false,
              placeholder: "All Service Providers",
              display: displaySelectedClinicianType.value,
              class: "z-1",
              disabled: reportIsStudentTotal.value,
              onChange: onClinicianChange
            }, null, 8, ["modelValue", "options", "loading", "option-disabled", "display", "disabled"]),
            (_unref(can)([_unref(PERMISSIONS).REPORTS_SELECT_DATES_VIEW]))
              ? (_openBlock(), _createBlock(_component_AppDateRangePicker, {
                  key: 2,
                  modelValue: _unref(reportStore).filters.dates,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(reportStore).filters.dates) = $event)),
                  value: _unref(reportStore).filters.dates,
                  disabled: reportIsPayroll.value || reportIsStudentTotal.value,
                  onChange: dateRangeChange
                }, null, 8, ["modelValue", "value", "disabled"]))
              : _createCommentVNode("", true)
          ])
        ], 512), [
          [_vShow, !reportIsStudentTotal.value]
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_component_RouterView, {
          onDefineQueryParams: _unref(reportStore).defineQueryParams
        }, null, 8, ["onDefineQueryParams"])
      ])
    ])
  ]))
}
}

})